import axios from "axios";
import { debounce } from "lodash";
import moment from "moment";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { Download, RefreshCw, Tag, Users, DollarSign, TrendingUp } from "react-feather";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
} from "reactstrap";
import Swal from "sweetalert2";
import { baseURL } from "../../../../Services/api/baseURL";
import Loader from "../../../Loader/Loader";

const CouponUsageAnalytics = () => {
    // ---------- state ----------
    const [usage, setUsage] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    // filters
    const [searchTerm, setSearchTerm] = useState("");
    const [fromDate, setFromDate] = useState(
        moment().subtract(30, "days").format("YYYY-MM-DD")
    );
    const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
    const [couponFilter, setCouponFilter] = useState("");

    // ---------- token ----------
    useEffect(() => {
        const t = JSON.parse(localStorage.getItem("token"));
        setToken(t || null);
    }, []);

    // ---------- debounced search ----------
    const debouncedSearch = useRef(
        debounce((value) => setSearchTerm(value), 300)
    ).current;

    // ---------- fetch coupons (for the filter dropdown) ----------
    const getCoupons = async () => {
        try {
            const t = JSON.parse(localStorage.getItem("token"));
            const res = await axios.get(`${baseURL}/api/admin/coupan`, {
                headers: { Authorization: `${t}` },
            });
            console.log("coupons fetch", res);
            if (res?.data?.success) {
                setCoupons(res?.data?.data || []);
            }
        } catch (e) {
            // non-fatal
            console.log("coupon list error", e);
        }
    };

    // ---------- fetch usage data ----------
    const getUsageData = async () => {
        const t = JSON.parse(localStorage.getItem("token"));
        try {
            setIsLoading(true);

            const params = new URLSearchParams();
            if (fromDate) params.append("from", fromDate);
            if (toDate) params.append("to", toDate);
            if (couponFilter) params.append("coupon", couponFilter);
            if (searchTerm) params.append("search", searchTerm);

            // Adjust endpoint as needed for your backend.
            const endPoint = `/api/admin/coupan/usage?${params.toString()}`;

            const response = await axios.get(`${baseURL}${endPoint}`, {
                headers: { Authorization: `${t}` },
            });
            console.log("usage fetch", response);
            if (response?.data?.success) {
                // Expected shape: array of orders (or a flat "usage" array)
                const rows = response?.data?.data || [];
                setUsage(Array.isArray(rows) ? rows : []);
            } else {
                setUsage([]);
            }
        } catch (error) {
            console.log("usage fetch error", error);
            setUsage([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getCoupons();
    }, []);

    useEffect(() => {
        if (token !== null || localStorage.getItem("token")) {
            getUsageData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, fromDate, toDate, couponFilter]);

    // ---------- KPIs ----------
    const stats = useMemo(() => {
        const totalRedemptions = usage.length;
        const totalDiscount = usage.reduce(
            (sum, o) => sum + (Number(o?.couponAmount) || 0),
            0
        );
        const uniqueCustomers = new Set(
            usage
                .map((o) =>
                    typeof o?.user_id === "object" ? o?.user_id?._id : o?.user_id
                )
                .filter(Boolean)
        ).size;
        const avgDiscount = totalRedemptions
            ? totalDiscount / totalRedemptions
            : 0;

        return {
            totalRedemptions,
            totalDiscount,
            uniqueCustomers,
            avgDiscount,
        };
    }, [usage]);


    const getCustomerName = (row) => {
        const u = row?.user_id;
        if (!u) return "—";
        if (typeof u === "string") return u;
        const name = [u?.first_name, u?.last_name].filter(Boolean).join(" ").trim();
        return name || u?.email || u?.phoneNumber || "—";
    };

    const getCustomerEmail = (row) => {
        const u = row?.user_id;
        if (!u || typeof u === "string") return "—";
        return u?.email || u?.phoneNumber || "—";
    };

    const getCouponName = (row) => {
        const c = row?.coupon;
        if (!c) return "—";
        if (typeof c === "string") {
            const match = coupons.find((x) => x?._id === c);
            return match?.coupan_name || "—";
        }
        return c?.coupan_name || "—";
    };

    const getCouponType = (row) => {
        const c = row?.coupon;
        if (!c || typeof c === "string") {
            const match = coupons.find((x) => x?._id === c);
            return match?.coupan_value_type || "";
        }
        return c?.coupan_value_type || "";
    };

    // ---------- export ----------
    const handleExport = () => {
        if (!usage.length) {
            Swal.fire({
                title: "Nothing to export",
                text: "No coupon usage data for the selected filters.",
                icon: "info",
                confirmButtonColor: "#d3178a",
            });
            return;
        }

        try {
            setExporting(true);

            const headers = [
                "Order #",
                "Coupon",
                "Coupon Type",
                "Customer",
                "Email",
                "Order Value",
                "Discount Given",
                "Order Status",
                "Payment Status",
                "Source",
                "Used On",
            ];

            const escape = (val) => {
                const s = val === null || val === undefined ? "" : String(val);
                // escape quotes, wrap if needed
                if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
                return s;
            };

            const rows = usage.map((o) => [
                o?.sequence_number || o?._id || "",
                getCouponName(o),
                getCouponType(o),
                getCustomerName(o),
                getCustomerEmail(o),
                o?.order_value ?? 0,
                o?.couponAmount ?? 0,
                o?.order_status || "",
                o?.transaction_status || "",
                o?.orderSource || "",
                o?.createdAt ? moment(o.createdAt).format("YYYY-MM-DD HH:mm") : "",
            ]);

            const csv = [headers, ...rows]
                .map((r) => r.map(escape).join(","))
                .join("\n");

            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `coupon-usage_${fromDate}_to_${toDate}.csv`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            Swal.fire({
                title: "Export failed",
                text: e?.message || "Something went wrong",
                icon: "error",
                confirmButtonColor: "#d3178a",
            });
        } finally {
            setExporting(false);
        }
    };

    // ---------- columns ----------
    const columns = [
        {
            name: "Order #",
            selector: (row) => row?.sequence_number || "",
            sortable: true,
            center: false,
            minWidth: "190px",
            cell: (row) => (
                <span className="font-medium">{row?.sequence_number || "—"}</span>
            ),
        },
        {
            name: "Coupon",
            selector: (row) => getCouponName(row),
            sortable: true,
            center: true,
            cell: (row) => (
                <span className="badge badge-light-primary" style={{ fontSize: 12 }}>
                    {getCouponName(row)}
                </span>
            ),
        },
        {
            name: "Customer",
            selector: (row) => getCustomerName(row),
            sortable: true,
            center: false,
            minWidth: "200px",
            cell: (row) => (
                <div style={{ lineHeight: 1.2, padding: "4px 0" }}>
                    <div style={{ fontWeight: 600 }}>{getCustomerName(row)}</div>
                    <div style={{ fontSize: 11, color: "#888" }}>
                        {getCustomerEmail(row)}
                    </div>
                </div>
            ),
        },
        {
            name: "Order Value",
            selector: (row) => row?.order_value || 0,
            sortable: true,
            center: true,
            cell: (row) => <span>${Number(row?.order_value || 0).toFixed(2)}</span>,
        },
        {
            name: "Discount",
            selector: (row) => row?.couponAmount || 0,
            sortable: true,
            center: true,
            cell: (row) => {
                const type = getCouponType(row);
                const symbol = type === "percent" ? "%" : "$";
                return (
                    <span className="text-success font-weight-bold">
                        {symbol === "$" ? "$" : ""}
                        {Number(row?.couponAmount || 0).toFixed(2)}
                        {symbol === "%" ? "%" : ""}
                    </span>
                );
            },
        },
        {
            name: "Order Status",
            selector: (row) => row?.order_status || "",
            sortable: true,
            center: true,
            cell: (row) => {
                const status = (row?.order_status || "").toLowerCase();
                const cls =
                    status === "delivered"
                        ? "badge-light-success"
                        : status === "cancelled"
                            ? "badge-danger"
                            : status === "pending"
                                ? "badge-light-warning"
                                : "badge-light-info";
                return (
                    <span className={`badge ${cls}`} style={{ fontSize: 12 }}>
                        {row?.order_status || "—"}
                    </span>
                );
            },
        },
        {
            name: "Source",
            selector: (row) => row?.orderSource || "",
            sortable: true,
            center: true,
            cell: (row) => (
                <span style={{ textTransform: "capitalize" }}>
                    {row?.orderSource || "—"}
                </span>
            ),
        },
        {
            name: "Used On",
            selector: (row) => row?.createdAt,
            sortable: true,
            center: true,
            minWidth: "170px",
            cell: (row) =>
                row?.createdAt
                    ? moment(row.createdAt).format("DD MMM YYYY, hh:mm A")
                    : "—",
        },
    ];

    // ---------- UI pieces ----------
    const StatCard = ({ icon: Icon, label, value, accent }) => (
        <Card
            style={{
                borderRadius: 12,
                border: "none",
                boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                height: "100%",
            }}
        >
            <CardBody style={{ padding: 18 }}>
                <div className="d-flex align-items-center">
                    <div
                        style={{
                            width: 48,
                            height: 48,
                            borderRadius: 12,
                            background: accent?.bg || "#fde4f1",
                            color: accent?.color || "#d3178a",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: 14,
                            flexShrink: 0,
                        }}
                    >
                        <Icon size={22} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                fontSize: 12,
                                color: "#7a7a7a",
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                fontWeight: 600,
                            }}
                        >
                            {label}
                        </div>
                        <div
                            style={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: "#1f1f1f",
                                marginTop: 2,
                            }}
                        >
                            {value}
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
    );

    return (
        <Fragment>
            <Container fluid style={{ paddingTop: 30 }}>
                {/* Title row */}
                <Row className="mb-3">
                    <Col>
                        <h5 className="mb-0 font-bold">Coupon Usage Analytics</h5>
                        <small style={{ color: "#888" }}>
                            See which customers redeemed coupons and the discount given.
                        </small>
                    </Col>
                </Row>

                {/* KPI cards */}
                <Row className="g-3">
                    <Col xs={6} md={3}>
                        <StatCard
                            icon={Tag}
                            label="Total Redemptions"
                            value={stats.totalRedemptions}
                            accent={{ bg: "#fde4f1", color: "#d3178a" }}
                        />
                    </Col>
                    <Col xs={6} md={3}>
                        <StatCard
                            icon={DollarSign}
                            label="Total Discount Given"
                            value={`$${stats.totalDiscount.toFixed(2)}`}
                            accent={{ bg: "#e3f7ec", color: "#11a452" }}
                        />
                    </Col>
                    <Col xs={6} md={3}>
                        <StatCard
                            icon={Users}
                            label="Unique Customers"
                            value={stats.uniqueCustomers}
                            accent={{ bg: "#eef2ff", color: "#4b5bdc" }}
                        />
                    </Col>
                    <Col xs={6} md={3}>
                        <StatCard
                            icon={TrendingUp}
                            label="Avg Discount / Order"
                            value={`$${stats.avgDiscount.toFixed(2)}`}
                            accent={{ bg: "#fff4e0", color: "#e08a00" }}
                        />
                    </Col>
                </Row>

                {/* Filters + Table */}
                <Row className="mt-3">
                    <Col sm="12">
                        <Card style={{ borderRadius: 12, border: "none" }}>
                            <CardBody style={{ padding: 15 }}>
                                <Form>
                                    <Row className="align-items-end g-2">
                                        <Col xs={12} md={6} lg={3}>
                                            <FormGroup className="mb-0">
                                                <Label className="font-semibold mb-1" style={{ fontSize: 13 }}>
                                                    From Date
                                                </Label>
                                                <Input
                                                    type="date"
                                                    value={fromDate}
                                                    max={toDate}
                                                    onChange={(e) => setFromDate(e.target.value)}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col xs={12} md={6} lg={3}>
                                            <FormGroup className="mb-0">
                                                <Label className="font-semibold mb-1" style={{ fontSize: 13 }}>
                                                    To Date
                                                </Label>
                                                <Input
                                                    type="date"
                                                    value={toDate}
                                                    min={fromDate}
                                                    max={moment().format("YYYY-MM-DD")}
                                                    onChange={(e) => setToDate(e.target.value)}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col xs={12} md={6} lg={3}>
                                            <FormGroup className="mb-0">
                                                <Label className="font-semibold mb-1" style={{ fontSize: 13 }}>
                                                    Coupon
                                                </Label>
                                                <Input
                                                    type="select"
                                                    value={couponFilter}
                                                    onChange={(e) => setCouponFilter(e.target.value)}
                                                >
                                                    <option value="">All Coupons</option>
                                                    {coupons.map((c) => (
                                                        <option key={c?._id} value={c?._id}>
                                                            {c?.coupan_name}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col xs={12} md={6} lg={3}>
                                            <FormGroup className="mb-0">
                                                <Label className="font-semibold mb-1" style={{ fontSize: 13 }}>
                                                    Search
                                                </Label>
                                                <div className="position-relative">
                                                    <i
                                                        className="fa fa-search"
                                                        style={{
                                                            position: "absolute",
                                                            left: 12,
                                                            top: "50%",
                                                            transform: "translateY(-50%)",
                                                            color: "#bbb",
                                                        }}
                                                    ></i>
                                                    <Input
                                                        type="text"
                                                        placeholder="Order #, customer, email..."
                                                        style={{ paddingLeft: 32 }}
                                                        onChange={(e) =>
                                                            debouncedSearch(e.target.value)
                                                        }
                                                    />
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row className="mt-3 align-items-center">
                                        <Col>
                                            <small style={{ color: "#888" }}>
                                                Showing{" "}
                                                <strong>{usage.length}</strong> redemption
                                                {usage.length === 1 ? "" : "s"} from{" "}
                                                <strong>
                                                    {moment(fromDate).format("DD MMM YYYY")}
                                                </strong>{" "}
                                                to{" "}
                                                <strong>
                                                    {moment(toDate).format("DD MMM YYYY")}
                                                </strong>
                                            </small>
                                        </Col>
                                        <Col xs="auto" className="d-flex gap-2">
                                            <Button
                                                color="light"
                                                className="d-flex align-items-center"
                                                onClick={getUsageData}
                                                disabled={isLoading}
                                            >
                                                <RefreshCw
                                                    size={15}
                                                    className="me-2"
                                                    style={{
                                                        animation: isLoading
                                                            ? "spin 1s linear infinite"
                                                            : "none",
                                                    }}
                                                />
                                                Refresh
                                            </Button>
                                            <Button
                                                color="primary"
                                                className="d-flex align-items-center"
                                                onClick={handleExport}
                                                disabled={exporting || isLoading}
                                            >
                                                <Download size={15} className="me-2" />
                                                {exporting ? "Exporting..." : "Export CSV"}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>

                            <DataTable
                                data={usage}
                                columns={columns}
                                striped
                                pagination
                                paginationPerPage={25}
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                progressPending={isLoading}
                                progressComponent={<Loader />}
                                noDataComponent={
                                    <div style={{ padding: 40, textAlign: "center" }}>
                                        <Tag size={28} color="#d3178a" />
                                        <div
                                            style={{
                                                marginTop: 10,
                                                color: "#555",
                                                fontWeight: 600,
                                            }}
                                        >
                                            No coupon usage found
                                        </div>
                                        <small style={{ color: "#999" }}>
                                            Try expanding the date range or clearing filters.
                                        </small>
                                    </div>
                                }
                                highlightOnHover
                                responsive
                            />
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* tiny spin keyframes for the refresh icon */}
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(360deg); }
                }
            `}</style>
        </Fragment>
    );
};

export default CouponUsageAnalytics;